import { Icon, Tooltip, Typography } from "@/components/atoms";
import { DataTableColumnDef } from "@/components/molecules";
import { FixedBaseInterval, FixedIntervals } from "@/redux/slices/trip/types";
import { addToast, formatCurrency, kmToUnit } from "@/utils";
import { StateRatesEditTableData, StateRatesViewTableData } from "./types";

export const generateViewColumns = (distanceUnit: string) => {
  const columns: DataTableColumnDef<StateRatesViewTableData>[] = [
    {
      accessorKey: "rangeIntervals",
      header: "Range Intervals",
      cell: (props) => props.row.original.rangeIntervals,
      size: 250,
      enableSorting: false,
    },
    {
      accessorKey: "range",
      header: `Range (${distanceUnit})`,
      cell: (props) => props.row.original.range,
      enableSorting: false,
    },
    {
      accessorKey: "baseTripCost",
      header: "Base Trip Cost",
      cell: (props) => props.row.original.baseTripCost,
      size: 200,
      enableSorting: false,
    },
    {
      accessorKey: "managementFee",
      header: () => (
        <div className="flex flex-row items-center gap-2">
          <Typography variant="action">Management Fee (Optional)</Typography>
          <Tooltip content="Management fee is fully paid to the Operator" placement="bottom">
            <span>
              <Icon name="InfoCircle" size="md" className="rotate-180" />
            </span>
          </Tooltip>
        </div>
      ),
      cell: (props) => props.row.original.managementFee,
      size: 250,
      enableSorting: false,
    },
    {
      accessorKey: "totalTripCost",
      header: "Total Trip Cost",
      cell: (props) => props.row.original.totalTripCost,
      size: 200,
      enableSorting: false,
    },
  ];

  return columns;
};

export const generateViewData = (baseInterval: FixedBaseInterval, intervals: FixedIntervals, currency: string, distanceUnit: string) => {
  const jointIntervals = baseInterval ? [baseInterval, ...intervals] : [];
  const data = jointIntervals.map(
    (interval, i) =>
      ({
        rangeIntervals: `Range ${i + 1}`,
        range: `${i > 0 ? kmToUnit(jointIntervals[i - 1].maximumRange, distanceUnit) : 0} - ${kmToUnit(
          interval.maximumRange,
          distanceUnit
        )} ${distanceUnit}`,
        baseTripCost: formatCurrency(interval.intervalCost, 2, currency, { skipDecimalConversion: true }),
        managementFee:
          interval.managementCostUnit === "percentage"
            ? `${interval.managementCost}%`
            : formatCurrency(interval.managementCost, 2, currency, { skipDecimalConversion: true }),
        totalTripCost: getTotalTripCost(interval.intervalCost, interval.managementCost, currency, interval.managementCostUnit),
      } as StateRatesViewTableData)
  );
  return data;
};

export const generateEditColumns = (distanceUnit: string) => {
  const columns: DataTableColumnDef<StateRatesEditTableData>[] = [
    {
      accessorKey: "rangeIntervals",
      header: "Range Intervals",
      cell: (props) => props.row.original.rangeIntervals,
      size: 250,
      enableSorting: false,
    },
    {
      accessorKey: "range",
      header: `Range (${distanceUnit})`,
      cell: (props) => props.row.original.range,
      enableSorting: false,
    },
    {
      accessorKey: "baseTripCost",
      header: "Base Trip Cost",
      cell: (props) => props.row.original.baseTripCost,
      size: 200,
      enableSorting: false,
    },
    {
      accessorKey: "managementFee",
      header: () => (
        <div className="flex flex-row items-center gap-2">
          <Typography variant="action">Management Fee (Optional)</Typography>
          <Tooltip content="Management fee is fully paid to the Operator" placement="bottom">
            <span>
              <Icon name="InfoCircle" size="md" className="rotate-180" />
            </span>
          </Tooltip>
        </div>
      ),
      cell: (props) => props.row.original.managementFee,
      size: 250,
      enableSorting: false,
    },
    {
      accessorKey: "totalTripCost",
      header: "Total Trip Cost",
      cell: (props) => props.row.original.totalTripCost,
      size: 200,
      enableSorting: false,
    },
    {
      accessorKey: "action",
      header: "",
      cell: (props) => props.row.original.action,
      size: 75,
      enableSorting: false,
    },
  ];

  return columns;
};

export const validateRanges = (ranges: number[]): boolean => {
  let rangeError = 0;
  const isIncremental = ranges.slice(1).every((item, i) => {
    const condition = ranges[i] < item;
    if (!condition) rangeError = i + 1;

    return condition;
  });

  if (!isIncremental) {
    addToast("danger", `Unable to save changes. Range ${rangeError} must be incremental over the previous range.`);
    return false;
  }

  return true;
};

export const getManagementCostUnitSymbol = (currencySymbol: string, unit?: string | null) => (unit === "percentage" ? "%" : currencySymbol);

export const getTotalTripCost = (baseTripCost: number, managementFee: number, currency: string, unit?: string | null) =>
  unit === "percentage"
    ? formatCurrency(baseTripCost + baseTripCost * (managementFee / 100), 2, currency, { skipDecimalConversion: true })
    : formatCurrency(baseTripCost + managementFee, 2, currency, { skipDecimalConversion: true });
